import React, { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { ArrowBack } from '@mui/icons-material';
import {
  getCertificationDisplayName,
  getCertificationSignalCopy
} from '../../constants/certificationBadgeCopy';
import { sendRequest } from '../../utils/dateHelpers';
import { buildCertificationProofUrl } from '../../utils/certificationLinks';

const CertificationMeaningPage = () => {
  const { proofId = '' } = useParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [badge, setBadge] = useState(null);

  useEffect(() => {
    let isMounted = true;

    const fetchProof = async () => {
      try {
        setLoading(true);
        setError('');
        const response = await sendRequest(
          `/certifications/public?proofID=${encodeURIComponent(
            proofId
          )}&view=full`
        );

        if (!isMounted) {
          return;
        }

        setBadge(response?.data || null);
      } catch (fetchError) {
        if (isMounted) {
          setError(fetchError?.message || 'Unable to load this certification.');
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    if (proofId) {
      fetchProof();
    } else {
      setLoading(false);
      setError('Proof ID is missing.');
    }

    return () => {
      isMounted = false;
    };
  }, [proofId]);

  const displayName = useMemo(
    () => getCertificationDisplayName(badge),
    [badge]
  );
  const signalCopy = useMemo(() => getCertificationSignalCopy(badge), [badge]);
  const proofUrl = useMemo(
    () => buildCertificationProofUrl(proofId),
    [proofId]
  );
  const qualification = badge?.qualification_snapshot;
  const matchedCriteria = qualification?.matched_strengths || [];
  const formatSignal = signal => String(signal || '').replace(/_/g, ' ');

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-slate-50 px-4 py-10 text-slate-900">
        <div className="w-full max-w-3xl animate-pulse rounded-[32px] border border-slate-200 bg-white p-8 shadow-sm">
          <div className="h-10 w-2/3 rounded bg-slate-200" />
          <div className="mt-6 h-4 w-full rounded bg-slate-200" />
          <div className="mt-3 h-4 w-5/6 rounded bg-slate-200" />
        </div>
      </div>
    );
  }

  if (error || !badge) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-slate-50 px-4 py-10 text-slate-900">
        <div className="max-w-xl rounded-[32px] border border-slate-200 bg-white p-8 text-center shadow-sm">
          <h1 className="text-2xl font-semibold">Meaning unavailable</h1>
          <p className="mt-3 text-sm leading-6 text-slate-600">
            {error || 'This certification explanation could not be found.'}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 px-4 py-10 text-slate-900">
      <div className="mx-auto flex w-full max-w-4xl flex-col gap-6">
        <div className="flex items-center justify-between gap-4">
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.22em] text-slate-500">
              Recognition meaning
            </p>
            <h1 className="mt-2 text-3xl font-semibold leading-tight text-slate-950 sm:text-4xl">
              {displayName}
            </h1>
          </div>
          <a
            href={proofUrl}
            className="inline-flex items-center gap-2 rounded-2xl border border-slate-300 px-4 py-2 text-sm font-semibold text-slate-800 transition hover:bg-slate-100"
          >
            <ArrowBack sx={{ fontSize: 18 }} />
            Back to proof
          </a>
        </div>

        <div className="rounded-[32px] border border-slate-200 bg-white p-7 shadow-sm">
          <p className="text-sm leading-7 text-slate-700">
            {qualification
              ? 'This achievement was verified from deterministic engineering signals in the evaluated 12-month period.'
              : signalCopy.overview}
          </p>
          <p className="mt-4 text-sm leading-7 text-slate-600">
            {qualification
              ? 'The criteria below were matched without a relevant blocking watchout.'
              : signalCopy.detail}
          </p>

          <div className="mt-8">
            <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">
              {signalCopy.heading}
            </p>
            <div className="mt-4 space-y-4">
              {(qualification ? matchedCriteria : signalCopy.bullets).map(
                item => (
                  <div
                    key={item}
                    className="flex items-start gap-3 text-sm leading-7 text-slate-700"
                  >
                    <span className="mt-[10px] h-2 w-2 rounded-full bg-slate-900" />
                    <span>{qualification ? formatSignal(item) : item}</span>
                  </div>
                )
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CertificationMeaningPage;
